import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useEffect, useMemo } from 'react';

import { bimCalculationRequisitesGql, bimOrderRequisitesGql } from './bimWorkOrderRequisitesQueries';

interface IUseRequisitesDefaultValue {
    orderId: number;
    isModalOpened: boolean;
    isFrameContract: boolean;
    projectDataStatus: RequestStatus;
}

export const useRequisitesDefaultValue = (props: IUseRequisitesDefaultValue) => {
    const { orderId, isModalOpened, isFrameContract, projectDataStatus } = props;

    const getOrderInfoVariables = useMemo(() => ({ workOrderId: orderId }), [orderId]);
    const withEffectBase = useMemo(
        () => projectDataStatus === RequestStatus.SUCCESS && isModalOpened && !!orderId,
        [projectDataStatus, isModalOpened, orderId],
    );

    const [getOrderInfo, getOrderStatus] = useRequest({
        requestId: 'bimWorkOrderRequisites',
        query: bimOrderRequisitesGql,
        withEffect: withEffectBase && isFrameContract,
        variables: getOrderInfoVariables,
    });

    const [getOrderCalcInfo, getOrderCalcStatus] = useRequest({
        requestId: 'bimWorkOrderRequisites',
        query: bimCalculationRequisitesGql,
        withEffect: withEffectBase && !isFrameContract,
        variables: getOrderInfoVariables,
    });

    const [makeResetData] = useResetStoreData({
        requestId: 'bimWorkOrderRequisites',
    });

    useEffect(() => {
        if (!isModalOpened) {
            makeResetData(undefined);
        }
    }, [isModalOpened, makeResetData]);

    return useMemo(
        () => ({
            defaultData: isFrameContract ? getOrderInfo?.bimWorkOrder?.data : getOrderCalcInfo?.bimCalculation?.data,
            isOrderInfoFetching: isFrameContract
                ? getOrderStatus === RequestStatus.FETCHING
                : getOrderCalcStatus === RequestStatus.FETCHING,
        }),
        [
            getOrderCalcInfo?.bimCalculation?.data,
            getOrderCalcStatus,
            getOrderInfo?.bimWorkOrder?.data,
            getOrderStatus,
            isFrameContract,
        ],
    );
};
